import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Compartment, EditorState } from '@codemirror/state';
import { EditorView, highlightActiveLine, highlightActiveLineGutter, keymap, lineNumbers, placeholder } from '@codemirror/view';
import { syntaxHighlighting, defaultHighlightStyle } from '@codemirror/language';
import { json } from '@codemirror/lang-json';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import {
  autocompletion,
  closeBrackets,
  closeBracketsKeymap,
  type CompletionContext,
  type CompletionResult,
} from '@codemirror/autocomplete';

@Component({
  selector: 'ui-code-editor',
  standalone: true,
  template: `<div #host></div>`,
  styles: [`
    :host { display: block; }
    :host ::ng-deep .cm-editor {
      border: 1px solid color-mix(in oklch, var(--color-base-content) 15%, transparent);
      border-radius: 0.5rem;
      font-size: 13px;
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
      background: var(--color-base-300);
    }
    :host ::ng-deep .cm-editor.cm-focused {
      outline: 2px solid color-mix(in oklch, var(--color-primary) 50%, transparent);
      outline-offset: 0;
      border-color: color-mix(in oklch, var(--color-primary) 40%, transparent);
    }
    :host ::ng-deep .cm-scroller { background: var(--color-base-300); }
    :host ::ng-deep .cm-content {
      min-height: var(--cm-min-height, 80px);
      padding: 10px 8px;
      caret-color: var(--color-primary);
      background: var(--color-base-300);
    }
    :host ::ng-deep .cm-gutters {
      border-right: 1px solid color-mix(in oklch, var(--color-base-content) 10%, transparent);
      background: var(--color-base-300);
      color: color-mix(in oklch, var(--color-base-content) 30%, transparent);
      font-size: 11px;
      border-radius: 0.5rem 0 0 0.5rem;
    }
    :host ::ng-deep .cm-activeLine { background: color-mix(in oklch, var(--color-base-content) 5%, transparent); }
    :host ::ng-deep .cm-activeLineGutter { background: color-mix(in oklch, var(--color-base-content) 7%, transparent); }
    :host ::ng-deep .cm-placeholder {
      color: color-mix(in oklch, var(--color-base-content) 35%, transparent);
      font-style: italic;
    }
    :host ::ng-deep .cm-tooltip-autocomplete {
      border: 1px solid color-mix(in oklch, var(--color-base-content) 12%, transparent);
      border-radius: 0.375rem;
      background: var(--color-base-100);
      box-shadow: 0 4px 12px color-mix(in oklch, var(--color-base-content) 10%, transparent);
    }
    :host ::ng-deep .cm-tooltip-autocomplete ul li {
      padding: 4px 10px;
      font-size: 12px;
    }
    :host ::ng-deep .cm-tooltip-autocomplete ul li[aria-selected] {
      background: color-mix(in oklch, var(--color-primary) 15%, transparent);
      color: var(--color-primary);
    }
    :host ::ng-deep .cm-completionLabel { color: color-mix(in oklch, var(--color-base-content) 85%, transparent); }
    :host ::ng-deep .cm-completionDetail { color: color-mix(in oklch, var(--color-base-content) 45%, transparent); font-style: italic; margin-left: 6px; }
  `],
})
export class UiCodeEditorComponent implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('host', { static: true }) host!: ElementRef<HTMLDivElement>;
  @Input() value = '';
  @Output() valueChange = new EventEmitter<string>();
  @Input() minHeight = '80px';
  @Input() placeholderText = '';
  @Input() completions: string[] = [];
  @Input() readonly = false;
  @Input() lang: 'json' | null = null;

  private view?: EditorView;
  private suppressEmit = false;
  private readonlyCompartment = new Compartment();

  ngAfterViewInit(): void {
    this.host.nativeElement.style.setProperty('--cm-min-height', this.minHeight);

    const source = (context: CompletionContext): CompletionResult | null => {
      const word = context.matchBefore(/\$?[\w.]+/);
      if (!word || (word.from === word.to && !context.explicit)) return null;

      const typed = word.text;
      const typedLower = typed.toLowerCase();
      const paths = this.completions ?? [];

      const options = paths
        .filter(p => {
          const pl = p.toLowerCase();
          return typed.includes('.') ? pl.startsWith(typedLower) : pl.includes(typedLower);
        })
        .map(p => ({ label: p, type: 'variable' as const }));

      return options.length ? { from: word.from, options } : null;
    };

    const extensions = [
      history(),
      keymap.of([...defaultKeymap, ...historyKeymap, ...closeBracketsKeymap]),
      EditorView.lineWrapping,
      lineNumbers(),
      closeBrackets(),
      highlightActiveLine(),
      highlightActiveLineGutter(),
      autocompletion({ override: [source], activateOnTyping: true }),
      EditorView.updateListener.of(update => {
        if (update.docChanged && !this.suppressEmit) {
          this.valueChange.emit(update.state.doc.toString());
        }
      }),
      ...(this.placeholderText ? [placeholder(this.placeholderText)] : []),
      ...(this.lang === 'json' ? [json(), syntaxHighlighting(defaultHighlightStyle)] : []),
      this.readonlyCompartment.of(EditorState.readOnly.of(this.readonly)),
    ];

    const state = EditorState.create({ doc: this.value ?? '', extensions });
    this.view = new EditorView({ state, parent: this.host.nativeElement });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['minHeight'] && this.host?.nativeElement) {
      this.host.nativeElement.style.setProperty('--cm-min-height', this.minHeight);
    }
    if (changes['value'] && this.view) {
      const current = this.view.state.doc.toString();
      if (current !== (this.value ?? '')) {
        this.suppressEmit = true;
        this.view.dispatch({
          changes: { from: 0, to: current.length, insert: this.value ?? '' },
        });
        this.suppressEmit = false;
      }
    }
    if (changes['readonly'] && this.view) {
      this.view.dispatch({
        effects: this.readonlyCompartment.reconfigure(EditorState.readOnly.of(this.readonly)),
      });
    }
  }

  ngOnDestroy(): void {
    this.view?.destroy();
  }
}
